/**
 * Deckungsbeitrag app types (#1781) — the FE projection of the
 * ``fact_type: "deckungsbeitrag"`` derived fact the worker materialiser
 * emits per (crop_cycle, period) (see
 * ``worker/src/services/materialisers/deckungsbeitrag.py``,
 * ``compute_deckungsbeitrag`` return dict). Money is EUR throughout;
 * ``db1_per_ha`` is null whenever the fact-time ``area_ha`` was unknown —
 * the ranking renders those last with an em dash, never NaN.
 */

/** The direct-cost buckets, mirroring the materialiser's ``_DIRECT_COST_BUCKETS``. */
export interface DirectCosts {
  saatgut: number;
  duenger: number;
  psm: number;
  loehne: number;
  maschinen: number;
  sonstige: number;
  /**
   * HOF-518 — Landkosten (Pacht), the seventh bucket: ``area_ha`` x the ``pacht_flaeche``
   * tile's €/ha, imputed on EVERY hectare including the owned ones (KB §II.5), plus any
   * ``exp_lease`` money the farm actually booked onto the cycle.
   *
   * 0 on every fact materialised before HOF-518 and on every tenant whose curated books
   * have not been published — the mapper defaults it, so an old fact is read as "no land
   * cost booked" and the drawer keeps rendering the PENDING row rather than 0,00 €.
   */
  land: number;
  /**
   * The labour-flow refactor's wave 2 — five buckets, all 0 on every fact materialised
   * before it and on every tenant whose payroll never reaches a cycle. The mapper defaults them,
   * so an old fact reads as "no labour recorded" and the drawer keeps its PENDING rows
   * rather than showing 0,00 € — the distinction the whole wave exists to make.
   *
   * `pflueck` is harvest wage, split out of `loehne` because it is YIELD-DEPENDENT: it
   * belongs above "Leistung nach Pflücken" and Pflege does not. Keeping them in one
   * bucket is exactly what the old per-cycle wage lump did, and it is why a farmer could
   * not see what the harvest cost per kilo.
   */
  pflueck: number;
  /** Erntehelferversicherung, MEASURED per person-day, plus any employer-side surcharge. */
  nebenkosten: number;
  /** This cycle's share of the monthly packing-hall pool, allocated by picked kg. */
  verpackung: number;
  /** yield x the cooling reference rate. An ESTIMATE — see `kuehlung_estimated`. */
  kuehlung: number;
  /** Planting labour, beside the plant material rather than inside Löhne. */
  pflanzung: number;
  /**
   * Layer C „Investition" — AfA, Zinsansatz, Reparatur und Versicherung of the farm's
   * installations (Folientunnel, Stellage, Bewässerung …), the thirteenth bucket. Inside
   * ``dkfl`` (and so ``db1``) exactly like ``establishment_amortised``: the drawer prints it
   * as a line and never adds it a second time.
   *
   * Absent on every fact materialised before the line existed; the mapper defaults it to 0,
   * and a 0 with no ``investition`` block keeps the PENDING row rather than reading 0,00 €.
   */
  investition: number;
}

/** The € of one Investition class (or farm pool), by component. Every key is conditional. */
export interface InvestitionComponents {
  afa?: number;
  zinsen?: number;
  reparatur?: number;
  versicherung?: number;
  /** One combined figure where the source does not split the two (Ludger's 2,5 % rate). */
  reparatur_versicherung?: number;
  /** A Nutzungsentgelt (use charge) for a tunnel the farm uses but does not own. */
  nutzung?: number;
}

/** What a class's share is measured in: Fläche, laufende Meter or Pflanzen. */
export type InvestitionKey = 'ha' | 'lfm' | 'pflanzen';

/** The registered asset (a tunnel) that valued an ``asset:`` row. */
export interface AssetRef {
  entity_type: string;
  entity_id: string;
  name: string | null;
  number: number | null;
  field_id: string | null;
}

/** Why a registered asset carries no row: it could not be valued. */
export type UnvaluedReason = 'no_cost' | 'invalid_operands' | 'no_charge' | 'leased_not_valued';

/**
 * One installation class charged to this cycle.
 *
 * ``calculated`` — Ludger's Stellage formula, € per key unit and year: Investition ÷
 * Nutzungsdauer (AfA) + Zinssatz × halbe Investition + Satz × Investition, × ``key_value``.
 * ``booked`` — a farm-wide amount off the BWA, split over ONLY the cycles that carry the
 * class (Tunnel on Tunnelkulturen …) in proportion to their key: ``pool × key_value ÷
 * population_key_total``.
 */
export interface InvestitionClass {
  id: string;
  label: string;
  method: 'calculated' | 'booked';
  key: InvestitionKey;
  /** This cycle's ha / lfm / plants; null when unknown (then ``refused`` is ``no_key``). */
  key_value: number | null;
  components: InvestitionComponents;
  /** € for this cycle — the Σ of ``components``. 0 on a refused class. */
  total: number;
  estimated: boolean;
  tier: string;
  source: string | null;
  /** Present ⇒ ``total`` is 0 and the drawer shows the reason instead of a formula. */
  refused?: 'no_key' | 'no_population';
  // ── method "calculated" ───────────────────────────────────────────────────
  capex_per_unit?: number;
  useful_life_years?: number;
  interest_rate?: number;
  repair_insurance_rate?: number;
  /** € per key unit and year, per component. */
  per_unit?: { afa: number; zinsen: number; reparatur_versicherung: number };
  // ── method "booked" ───────────────────────────────────────────────────────
  pool?: InvestitionComponents;
  pool_total?: number;
  /** Σ key over every cycle carrying the class. */
  population_key_total?: number;
  /** ``key_value / population_key_total``. */
  share?: number;
  applies_to?: { crops: string[]; cultivation_systems: string[] };
  // ── asset rows (``source`` is ``asset:tunnel``) ───────────────────────────
  /** The asset that valued this row — present iff ``source`` is ``asset:tunnel``. */
  asset?: AssetRef;
  /** Owned assets carry the AfA formula; a use charge is a booked Nutzungsentgelt. */
  ownership?: 'owned' | 'use_charge' | 'leased';
  /** The acquisition is fully written off — only Reparatur and Versicherung remain. */
  paid_off?: boolean;
  /** Operands the engine filled with a default (``interest_rate`` …) rather than a farm value. */
  defaulted?: string[];
  /** The acquisition cost is a template reference value, not a farm figure. */
  capex_from_template?: boolean;
  acquired_at?: string | null;
  retired_at?: string | null;
  acquisition_cost_eur?: number;
  repair_rate?: number;
  insurance_rate?: number;
  /** The foil's replacement cost and cycle, when the tunnel carries a foil. */
  foil_cost_eur?: number | null;
  foil_cycle_years?: number | null;
  /** € per year of a Nutzungsentgelt row. */
  use_charge_eur_per_year?: number;
}

/** A farm pool charged to NO cycle — shown for transparency only, never in the DB. */
export interface InvestitionUnallocated {
  id: string;
  label: string;
  components: InvestitionComponents;
  total: number;
  source: string | null;
  reason: 'not_split' | 'no_population';
}

/** The Investition line's evidence: what each class charged, and what reached no cycle. */
export interface InvestitionBlock {
  /** == ``direct_costs.investition``. */
  total: number;
  estimated: boolean;
  classes: InvestitionClass[];
  unallocated: InvestitionUnallocated[];
  /** Template classes replaced by registered assets on this Schlag — named, never listed as rows. */
  suppressed_classes?: { id: string; label: string; replaced_by: string }[];
  /** Registered assets that could not be valued, and why. */
  unvalued?: { asset: Pick<AssetRef, 'entity_type' | 'entity_id' | 'name'>; reason: UnvaluedReason }[];
}

/** The `kind` a labour Posten item carries. Controlled vocabulary, never free text. */
export type LabourPostenKind =
  | 'picking'
  | 'insurance'
  | 'surcharge'
  | 'packing_labour'
  | 'packaging_tray'
  | 'packaging_crate'
  | 'packaging_pool_fee'
  | 'cooling'
  | 'planting'
  | 'field_care'
  | 'overhead'
  // HOF-644 — the farm's employer cost from payroll (LODAS), and labour with no field of
  // its own spread onto this cycle (a no-field pool, or a salaried person's crop share).
  | 'employer_uplift'
  // HOF-705 — decision B1: hours × the Personengruppe's average employer cost per hour, minus
  // the booked wage. On a month whose payroll import computed per-hour rates it replaces
  // ``employer_uplift``; it can be negative.
  | 'employer_hour_cost'
  | 'pool_share'
  | 'fixed_share';

/**
 * One month's worth of one kind of labour money on this cycle. Σ of a bucket's items is
 * that bucket — the drawer renders that as a reconciliation tick, so a mismatch is a
 * visible defect rather than a silent one.
 */
export interface LabourPostenItem {
  /** ``YYYY-MM``, or null for a period-level item (cooling, the surcharge). */
  month: string | null;
  kind: LabourPostenKind;
  amount: number;
  hours: number | null;
  kg: number | null;
  /** Distinct people — a COUNT. This entity never names or ranks an individual. */
  worker_count: number | null;
  /** Packing only: the fraction of `pool` this cycle took. */
  share: number | null;
  /** Packing only: the kilogrammes that earned the share. */
  basis: number | null;
  /** Packing only: the whole month's pool the share came out of. */
  pool: number | null;
  /**
   * Packaging MATERIAL only (``packaging_*`` kinds) — absent on every other item. The
   * article's stable key (``tray:500``, ``crate:holzkiste``, ``pool:ifco``) and the name the
   * worker prints it under; the pieces this cycle took (its share of the crop's delivered
   * pieces that month, by picked kg, with ``share``/``basis`` above saying which share);
   * and the € per piece off the farm's own purchase invoices. ``unit_price`` null = no
   * invoice priced the article, and the row then books 0,00 €.
   */
  article?: string | null;
  article_label?: string | null;
  pieces?: number | null;
  unit_price?: number | null;
  /** The year the price was read from; ``price_carried`` when that is an earlier season. */
  price_year?: number | null;
  price_carried?: boolean | null;
}

/** One packaging article's invoice price, as the Verpackung line used it. */
export interface VerpackungMaterialPrice {
  article: string;
  article_label: string | null;
  unit_price: number;
  year: number | null;
  /** True when no invoice of the season priced it and an earlier year's answered. */
  carried: boolean;
  pieces_bought: number | null;
  /** Invoice numbers behind the price. */
  documents: string[];
}

/**
 * The evidence behind the Verpackung line's MATERIAL: which invoice priced each article,
 * and which pieces no invoice priced. The second list is why the block exists — those
 * pieces book 0,00 €, so the line is a lower bound until the invoice is in the archive.
 */
export interface VerpackungMaterial {
  prices: VerpackungMaterialPrice[];
  unpriced: { article: string; article_label: string | null; pieces: number }[];
  /** Gebinde this cycle shipped that no delivery line named a crate for, or null. */
  gebinde_without_crate: number | null;
}

/** bucket → its monthly items. A bucket with no money is absent, never an empty list. */
export type LabourPosten = Partial<Record<keyof DirectCosts, LabourPostenItem[]>>;

/** What the cooling figure was computed from, so the drawer can show its Rechenweg. */
export interface KuehlungEstimate {
  rate: number;
  /** Always ``€/kg`` today; carried verbatim so a wrong unit is visible rather than assumed. */
  unit: string;
  /** The kilogrammes charged — the cycle's measured harvest. */
  quantity: number;
  source: string | null;
  /** ``template`` (the reference rate) or ``farm-confirmed`` (the farm's own). */
  tier: string;
}

/**
 * Labour money that reached no cost line, and why. On the FACT rather than in a log,
 * because the person who can fix it — someone adding a missing label to the
 * activity-classes document — reads the app, not ELK.
 */
/**
 * HOF-644 — wage booked WITHOUT its employer cost: the payroll book covers the month, but
 * the person found no Personengruppe or no rate. The labour lines read low by the missing
 * uplift on this wage. `pooled_wage_eur` is this cycle's share of such wage in pools.
 */
export interface LabourUpliftGap {
  records: number;
  hours: number;
  wage_eur: number;
  pooled_wage_eur: number;
}

export interface LabourUnbooked {
  eur: number;
  hours: number;
  /** activity_class → €. Today only `unclassified` can appear. */
  by_class: Record<string, number>;
  /** A month where packing happened but nothing in scope was picked to weight it by. */
  packing_unallocated?: number;
}

/**
 * One derived Bezugsgröße denominator's provenance (#2303): how ``row_length_m``
 * / ``plant_count`` were obtained — ``recorded`` (directly on the crop_cycle) or
 * derived from a geometry attribute (with the attribute value carried for the
 * honesty line). All params are optional; a defensive mapper only keeps finite
 * numbers.
 *
 * ``source`` is the open vocabulary ``recorded`` | ``derived_from_row_spacing`` |
 * ``derived_from_field_row_spacing`` (HOF-312 — the Reihenabstand recorded on the
 * SCHLAG, the lowest rung of the ``row_length_m`` ladder) | ``derived_from_plants_per_m``
 * | ``derived_from_plants_per_m2``. Deliberately typed ``string`` rather than a union:
 * a tier this app does not know must arrive as untrusted (``TRUSTED_GEOMETRY_SOURCES``),
 * not as a type error that never reaches a running page.
 *
 * ``tier`` qualifies HOW firmly the source value is held — HOF-312's field pitch carries
 * ``farm_confirmed`` because it is a farm convention an operator recorded, never a
 * measured row geometry. Absent on the tiers that predate it.
 */
export interface GeometryProvenanceEntry {
  source: string;
  tier?: string;
  row_spacing_m?: number;
  plants_per_m?: number;
  plants_per_m2?: number;
  /** ``derived_from_tunnels``: how many registered tunnels the metres were summed over. */
  tunnels?: number;
  /** ``derived_from_tunnels``: the Schlag's total running metres (Reihen × Länge). */
  field_row_length_m?: number;
}

/** How much of the Schlag's tunnel fleet stands on substrate — in metres, or in tunnels when unmeasured. */
export interface SubstrateEligibility {
  basis: 'metres' | 'count';
  eligible: number;
  total: number;
  unknown: number;
  fraction: number;
}

/**
 * The ``geometry_provenance`` block #2303 folds onto the fact when a Reihenmeter
 * / Pflanze denominator is derivable. ``comparability`` flags each derived basis
 * as ``"within_crop"`` (row geometry differs across crops → those bases rank only
 * within one Kultur). Absent on facts without derivable geometry.
 */
export interface GeometryProvenance {
  row_length_m?: GeometryProvenanceEntry;
  plant_count?: GeometryProvenanceEntry;
  comparability?: Record<string, string>;
  substrate_eligibility?: SubstrateEligibility;
}

/**
 * #2442 — one persisted Gemeinkosten (allocated-overhead) audit entry off the
 * fact's ``allocations`` array (materialiser ``gather_attributed_allocations``).
 * Each entry is one cost_center/place share that summed into ``allocated_overhead``;
 * the drill-down lists them and reconciles Σ ``amount`` back to the line. Defensive:
 * a malformed entry drops out of the mapper (never a NaN row).
 */
export interface AllocationEntry {
  target_crop_cycle_id: string | null;
  method: string | null;
  /** The split basis (area×duration weight); a number or null. */
  basis: number | null;
  amount: number;
  cost_center_id: string | null;
  /** 'manual' | 'place' | 'd2' — how the share was attributed. ('auto', the retired
   * cost_center overhead split, no longer occurs; pre-migration facts may still carry it.) */
  source: string | null;
}

/**
 * One D2 Absatz-pool line behind the Gemeinkosten figure — a Hütte, the Verkaufsbüro,
 * Werbung or Bankgebühren. The Deckungsbeitrag model (§II.1) shares exactly these:
 * ``Σ Poolkosten / Σ kg durch den Pool``, Absatz-side, full-cost view only.
 *
 * All FOUR slots always render. ``captured: false`` means nobody has modelled that pool
 * yet — which is a different statement from "this pool cost 0,00 €", and the drawer says
 * "noch nicht erfasst" for it, exactly as the Verpackung and Kühlung lines already do.
 * A fact with no pool captured omits ``d2_pools`` entirely, and ``toD2Pools`` fills the four
 * empty lines from ``D2_POOL_SLOTS`` below — so this array is ALWAYS four entries on a mapped
 * row, whatever the payload carried, and the line set never depends on it.
 */
export interface D2PoolEntry {
  slot: string;
  label: string;
  amount: number;
  /** The whole pool's € for the period — the numerator this cycle took a kg share of. */
  pool_total: number;
  /** This cycle's kg through the pool; null when the pool allocated nothing to it. */
  basis: number | null;
  share: number | null;
  method: string | null;
  captured: boolean;
}

/** The four D2 lines, in drawer order. Mirrors ``absatz_pools.D2_POOL_SLOTS``. */
export const D2_POOL_SLOTS: ReadonlyArray<{ slot: string; label: string }> = [
  { slot: 'huetten', label: 'Hütten' },
  { slot: 'verkaufsbuero', label: 'Verkaufsbüro' },
  { slot: 'werbung', label: 'Werbung' },
  { slot: 'bankgebuehren', label: 'Bankgebühren' },
];

/**
 * One per-hectare pooled share that landed in a DIRECT-COST bucket rather than in
 * Gemeinkosten: unpinned ``exp_fuel`` → Maschinen (over every cycle in the period), and
 * unpinned ``exp_energy`` → Sonstige Direktkosten (over the period's TUNNEL cycles only —
 * an open field draws no purchased power, so it takes no share and carries no entry).
 *
 * The caveat flags are the honest half. ``vehicle_split_pending``: without the
 * Kennzeichen/Marke key the model's §II.4 Feld-vs-Auslieferung split has not happened, so
 * delivery diesel is currently charged per hectare. ``hall_power_unsplit``: packing-hall
 * electricity is not tunnel electricity and today's ``exp_energy`` cannot tell them apart.
 */
export interface PooledHaAllocation {
  amount: number;
  pot_total: number;
  ledger_category: string | null;
  /** 'all' | 'tunnel' — which cycles shared the pot. */
  population: string | null;
  method: string | null;
  /** This cycle's ha — the weight it took its share on. */
  basis: number | null;
  share: number | null;
  vehicle_split_pending?: boolean;
  hall_power_unsplit?: boolean;
}

/**
 * A pot the period could not land on anybody — no kg through a pool, no area, no tunnel.
 * The Deckungsbeitrag model's reconciliation discipline (§II.3) requires the residual to
 * be VISIBLE rather than silently spread; the retired mechanic's selector guaranteed the
 * opposite, which is how a farm's whole diesel bill ended up on its berries.
 */
export interface UnallocatedPool {
  kind: string;
  amount: number;
  reason: string;
}

/**
 * #2487 Part B — one identified Pflanzgut input in the Saatgut/Pflanzen leg's breakdown:
 * the variety product, its €/unit, the planted quantity, the € it contributes (gross,
 * pre-amortisation), and where the price came from (a variety-product ``seed_price`` or
 * the crop-level estimate). Drives the drawer's "Süßkirschen-Bäume · 400 Stk × 18 €"
 * identification line. Defensive: a non-positive quantity/price drops out of the mapper.
 */
export interface SaatgutInput {
  variety: string | null;
  product_id: string | null;
  quantity: number;
  unit_price: number;
  /** The input's gross € (quantity × unit_price, pre-amortisation). */
  value: number;
  /** 'estimate' (crop-level price book) | 'myfarm24-price-list' (booked) | … */
  provenance: string | null;
  /**
   * #2538 — the Preisspiegel catalog item + observation day THIS input's €/unit came from.
   * Present only when the input fell back to the crop-level price book (an own-priced lot is
   * backed by no Preisspiegel observation). Both keys or neither.
   */
  catalog_item_id?: string;
  observation_date?: string;
}

/**
 * #2533-A/#2593 — where THIS crop-year sits in a perennial's productive stand life, as the
 * materialiser's ``_amortise_over_stand`` stamped it (``_STAND_*``). Mirrored by hand: no
 * cross-language import exists, so ``deckungsbeitragMappers.test.ts`` pins this list against
 * the Python source.
 *
 * ``fully_depreciated`` / ``not_yet_planted`` are the two states whose **0,00 € is the rule's
 * VERDICT** rather than a missing booking (the worker's ``_STAND_ZERO_STATES``) — the drawer
 * must explain those instead of accusing the data of being incomplete. The other three all
 * book a real per-year share and render exactly as a stand-less fact does.
 */
export const STAND_STATES = ['in_window', 'fully_depreciated', 'not_yet_planted', 'unknown', 'unknown_year'] as const;
export type StandState = (typeof STAND_STATES)[number];

/** #2593 — the two states whose 0,00 € the drawer must explain (worker ``_STAND_ZERO_STATES``). */
export const STAND_ZERO_STATES = ['fully_depreciated', 'not_yet_planted'] as const;
export type StandZeroState = (typeof STAND_ZERO_STATES)[number];

/**
 * #2533-A/#2593 — how much the Anlagejahr behind a stand verdict can be trusted
 * (``stand_register.EMITTED_STAND_PROVENANCES``). NOT a bool by design: an operator's guess
 * silently books 0,00 € on a productive stand once the year falls outside the window, so the
 * reader must be able to tell a stated year from an assumed one — the drawer names the tier.
 * ``unknown`` is engine-stamped (not authorable) and is the only tier in production today.
 */
export const STAND_PROVENANCES = ['farmer_stated', 'estimated', 'operator_assumed', 'unknown'] as const;
export type StandProvenance = (typeof STAND_PROVENANCES)[number];

/**
 * #2533-A/#2593 — the ``stand`` sub-block the materialiser folds onto ``saatgut_estimate``
 * to EXPLAIN the € it booked. ``stand_life_years`` is the RESOLVED span (a ``cleared_year``
 * wins over the catalog's nominal life), so ``gross / stand_life_years`` is what an in-window
 * year books. Every year field is nullable — a malformed one degrades to null, never NaN.
 */
export interface SaatgutStand {
  state: StandState;
  /** Anlagejahr (the register's, not the crop_cycle's ``planted_at``). */
  established_year: number | null;
  /** Rodungsjahr when the register carries one — it WINS over the nominal stand life. */
  cleared_year: number | null;
  /** The RESOLVED window span in years (what ``amortise_lifetime_years`` reports). */
  stand_life_years: number | null;
  /** 1-based Standjahr ordinal within the window; null outside it and on an unknown year. */
  stand_year: number | null;
  provenance: StandProvenance;
}

/**
 * #2593 — a stand narrowed to the two states whose 0,00 € is the rule's verdict.
 *
 * The zero-state copy says "abgeschrieben" / "vor der Pflanzung", which is a LIE about any
 * other state, so the narrowing is a type rather than a convention: handing an ``in_window``
 * stand to that copy is a compile error, not a wrong sentence over a real 1.200 € line. Only
 * ``zeroStateStand()`` mints one.
 */
export type ZeroStateStand = SaatgutStand & { state: StandZeroState };

/**
 * #2608 — the cost treatments a FACT may carry, mirroring the worker's
 * ``FACT_COST_TREATMENTS`` (a strict subset of the price book's own vocabulary — the legacy
 * ``amortise`` token is normalised worker-side and never reaches here). Mirrored by hand: no
 * cross-language import exists, so ``deckungsbeitragMappers.test.ts`` pins this list against
 * the Python source, exactly as it does for ``STAND_STATES``.
 */
export const SAATGUT_COST_TREATMENTS = ['annual', 'establishment'] as const;
export type SaatgutCostTreatment = (typeof SAATGUT_COST_TREATMENTS)[number];

/**
 * #2441/#2442 — the Saatgut/Pflanzgut ESTIMATE valuation folded onto the fact when
 * the seed cost is neither booked nor amortised (``saatgut = quantity(basis) ×
 * unit_price``). Present only when the estimate applied (key-absence == booked or
 * n/a). Drives the Saatgut line's formula breakdown (Fläche × Dichte = Pflanzen ×
 * Preis) and the "geschätzter Einstandspreis" badge.
 */
export interface SaatgutEstimate {
  /** 'pflanze' | 'gewicht' | 'einheit' — the price book's charging basis. */
  basis: string;
  unit_price: number;
  quantity: number;
  /** Price-book provenance label (e.g. the custom price source id). */
  source: string;
  /**
   * #2461/#2608 — how the Pflanzgut/Saatgut cost was TREATED, as the worker's
   * ``fact_cost_treatment`` stamped it:
   *   - ``establishment`` — a true perennial's Pflanzgut spread over its productive stand
   *     life; the line books only this year's share (``amortise_lifetime_years`` rides along).
   *   - ``annual`` — booked whole in this Wirtschaftsjahr, because the stand is replaced every
   *     year (long cane, tray strawberries) or the crop is arable. NO lifetime rides along —
   *     an annual leg has no stand life, and a "1" on the fact would be a divisor.
   * Absent on a pre-#2608 fact (every prod fact until the post-deploy re-materialise), so the
   * FE must treat key-absence as "the treatment is not known", never as annual.
   */
  cost_treatment?: SaatgutCostTreatment;
  /** #2461 — the stand life the establishment cost is spread over; present with 'establishment'. */
  amortise_lifetime_years?: number;
  /**
   * #2487 Part B — the per-input product breakdown (one entry per Pflanzgut lot). Present only
   * when the leg was backed by an identified Pflanzgut ``seed_lot``; absent on the #2441
   * geometry estimate, so a non-Pflanzgut cycle's block stays the pre-Part-B shape.
   */
  breakdown?: SaatgutInput[];
  /**
   * #2538 — the Preisspiegel catalog item + observation day the ``unit_price`` was published
   * under, so the Rechenweg's price anchor deep-links the exact line instead of the app root.
   * Absent when the resolved price-book entry names no item (legacy #2349 anchors, the WF
   * curation overlay) and on a blended multi-lot aggregate, whose price matches no single
   * observation — those keep the app-root link. Both keys or neither.
   */
  catalog_item_id?: string;
  observation_date?: string;
  /**
   * #2533-A/#2593 — where this crop-year sits in the perennial's stand life, and how much the
   * Anlagejahr behind that verdict can be trusted. Present whenever the leg was amortised over
   * a stand; absent on an annual estimate and whenever the emitted block is uninterpretable
   * (an unrecognised ``state`` degrades to key-absence, never to a fabricated zero verdict).
   */
  stand?: SaatgutStand;
}

/**
 * #2695 D/E — the MODELLED Leistung valuation folded onto the fact when a crop_cycle books
 * **no revenue at all** and its harvested quantity can be valued from the curated sale price
 * book (``config/agronomy/sale-price-estimates@v1.yaml``). Present only when the estimate
 * applied; key-absence == the Leistung is booked revenue (or there is none).
 *
 * 🔴 **This is not money the farm received.** Booked revenue fences the estimate — a single
 * attributed € and no estimate is produced — so a row carrying this block has a Leistung that
 * no buyer has paid. Every drawer surface that renders the € has to say so, because the row
 * sits in the same table as cycles whose Leistung is real.
 *
 * ⚠️ **BOTH operands carry their own unit and they vary INDEPENDENTLY**: the book prices
 * arable in ``€/t``, Kartoffeln in ``€/dt`` and berries in ``€/kg``, while the twin's yield is
 * ``dt`` for arable harvest and ``kg`` for the berry pick stream. The producer ships every
 * operand AND both units precisely so this app renders the derivation instead of re-deriving
 * it — assuming either unit is the 10x/100x defect class #2695 D exists to close, and
 * ``format.ts``'s ``formatQty``/``formatEurPerUnit`` must NOT be used on them (their
 * ``unitLabel`` collapses everything that is not ``dt`` to ``kg``; see ``formatBookPrice``).
 */
/**
 * HOF-518 — the Landkosten line's operands: one €/ha rate spread over the cycle's area.
 *
 * ``basis`` is the honest name for what that is — ``flat_per_hectare``, never a per-Flurstück
 * measurement — and it is carried rather than assumed because the day this app can charge a
 * measured per-Schlag rent, the same line must be able to say so.
 */
export interface LandEstimate {
  /** € per :attr:`unit`, off the ``pacht_flaeche`` tile. */
  unit_price: number;
  /** Always ``€/ha`` today; carried verbatim so a wrong unit is visible rather than assumed. */
  unit: string;
  /** The hectares charged — the cycle's own area, including any the farm OWNS. */
  area_ha: number;
  /** ``flat_per_hectare`` — one rate on every hectare, not a measurement. */
  basis: string;
  /** ``estimate:pacht_flaeche`` or ``observation:pacht_flaeche`` — which grade priced it. */
  source: string;
  /** True iff the rate is the curated reference rather than the farm's own contract. */
  estimate: boolean;
  /** The Preisspiegel tile the rate was read off, for the Rechenweg's deep link. */
  catalog_item_id: string;
  /** The observation's own day, so the deep link lands on the cell the number came from. */
  observation_date: string | null;
  /**
   * HOF-518 (codex r2) — present, and true, exactly when the tile carried MORE THAN ONE
   * Pachtvertrag and the engine refused to read any single one of them as the farm's rate.
   * Absent on every farm with one lease or none, so it is the difference between "published
   * no contract" and "published several we would not guess between".
   */
  contract_rates_ambiguous?: boolean;
}

/**
 * The Substrat charge's operands (2026-10-06): running metres × the rule's litres per running
 * metre = m³, × the ``substrat`` tile's € per m³. The charge is ALREADY inside
 * ``direct_costs.sonstige`` — this block explains part of that line, it never adds to it.
 *
 * Absent on every cycle no (crop, cultivation system) rule names: open field stands in soil,
 * and no rule exists for a blackberry pot.
 */
export interface SubstratEstimate {
  /** € per :attr:`unit`, off the ``substrat`` tile. */
  unit_price: number;
  /** Always ``€/m³`` today; carried verbatim so a € per litre (1.000x) is visible, not assumed. */
  unit: string;
  /** The running metres filled — the cycle's row length. */
  row_length_m: number;
  /** The Schlag's total running metres when only part of it stands on substrate. */
  total_row_length_m?: number;
  /** Tunnels whose grow system is unrecorded (assumed substrate). */
  grow_system_unknown?: number;
  /** What ``grow_system_unknown`` and the eligibility share count: metres or tunnels. */
  eligibility_basis?: 'metres' | 'count';
  /**
   * Where those metres came from (``recorded``, ``derived_from_row_spacing``,
   * ``derived_from_field_row_spacing`` …), or null when the fact does not say. The worker reads
   * a Schlag-derived row length here ON PURPOSE (the one declared exception to HOF-312's fence),
   * so the drawer says so when it did.
   */
  row_length_source: string | null;
  /** The rule's litres of substrate per running metre (Ludger: 19,2 l/lfm). */
  litres_per_row_m: number;
  /** ``row_length_m × litres_per_row_m ÷ 1000``, rounded to 3 decimals by the engine. */
  volume_m3: number;
  /** The rule's own provenance prose, or null. */
  rule_source: string | null;
  /** True iff EITHER operand is an estimate — drives the „geschätzt" badge. */
  estimate: boolean;
  /** The € per m³ is the curated reference rather than the farm's own price. */
  price_estimate: boolean;
  /** The litres per running metre is a template rather than a farm-confirmed figure. */
  rule_estimated: boolean;
  /** ``estimate:substrat`` or ``observation:substrat`` — which grade priced it. */
  source: string;
  /** The Preisspiegel tile the price was read off, for the deep link. */
  catalog_item_id: string;
  /** The observation's own day, so the deep link lands on the cell the number came from. */
  observation_date: string | null;
}

export interface LeistungEstimate {
  /** The estimated Leistung in €, as the engine computed and persisted it. Never re-derived here. */
  value: number;
  /** The book price, in ``price_unit``. */
  unit_price: number;
  /** The PRICE's own unit, verbatim off the fact ("€/t" | "€/dt" | "€/kg"). Never defaulted. */
  price_unit: string;
  /** The harvested quantity that was valued, in ``quantity_unit``. */
  quantity: number;
  /** The YIELD's own unit, verbatim off the fact ("dt" | "kg" | "t"). Never defaulted. */
  quantity_unit: string;
  /** Price-book provenance token (``estimate:sale/<crop>``). */
  source: string;
  /**
   * #2538 seam — the Preisspiegel catalog item + observation day the book price is published
   * under, so the Rechenweg's price anchor deep-links the exact line. Both keys or neither.
   */
  catalog_item_id?: string;
  observation_date?: string;
  /** The book row's own justification prose, so the drawer can say WHY this € and not another. */
  anchor?: string;
}

/**
 * HOF-21 — why one Leistung-Posten carries no Preisspiegel price.
 *
 * HOF-102 changed what a refusal COSTS, not what it is called: under the one path a
 * refused Posten contributes 0 € (D2) rather than keeping the booked €, so the tokens
 * are unchanged but every string that explains them had to be rewritten — see
 * ``leistungMark``.
 *
 * A closed set, mirroring the worker's own vocabulary verbatim. It is a union rather
 * than ``string`` so a reason the copy map does not name fails ``tsc`` here instead of
 * reaching a farmer as a bare token like ``no_catalog_item``.
 */
export const VALUATION_REFUSALS = [
  'multi_item',
  'no_catalog_item',
  'no_month',
  'no_quantity',
  'no_price',
  'unconvertible_unit',
  'not_netto',
  'mixed_unit',
  'incomplete',
] as const;
export type ValuationRefusal = (typeof VALUATION_REFUSALS)[number];

/**
 * HOF-21/HOF-102 — what one Posten's € IS, once the valuation is done with it.
 *
 * ``gebucht`` is gone and ``nicht_bewertet`` took its slot, verbatim from the worker's
 * ``BASIS_UNVALUED``. That is the whole change in one token: a Posten the price book
 * cannot answer for used to KEEP the booked €, and now carries none at all.
 */
export const POSTEN_BASES = ['nicht_bewertet', 'preisspiegel_monat', 'estimate'] as const;
export type PostenBasis = (typeof POSTEN_BASES)[number];

/**
 * HOF-307 (S4 of HOF-299) — why a MATERIAL INPUT (a Düngung's or Pflanzenschutz's article)
 * carries no €, verbatim from the worker's ``input_valuation`` vocabulary. Closed, so a
 * token the copy map does not name fails ``tsc`` rather than reaching a farmer bare.
 */
export const INPUT_REFUSALS = [
  'no_article',
  'no_quantity',
  'no_product',
  'no_price_for_product',
  'unconvertible_unit',
] as const;
export type InputRefusal = (typeof INPUT_REFUSALS)[number];

/**
 * HOF-307 — what one input's € IS: the cost the ingest booked off the year's price list
 * (``booked``), the farm's OWN purchase price on the product tile (``observed``), the
 * Erntejahr's MyFarm24 list price (``list_price``), the curated reference price
 * (``estimate``) — or nothing (``unvalued``), which contributes 0 € and says why.
 */
export const INPUT_POSTEN_BASES = ['booked', 'observed', 'list_price', 'estimate', 'unvalued'] as const;
export type InputPostenBasis = (typeof INPUT_POSTEN_BASES)[number];

/**
 * HOF-307 — one material input of one operation, valued down the buy-side cascade.
 * ``amount`` is authoritative and never re-multiplied here (the worker owns the g/kg/dt
 * and ml/l conversion); a refused input has ``amount: 0`` and a ``reason``.
 */
export interface InputPostenItem {
  operation_id: string;
  /** The article as the journal spells it — the row's own name. */
  article: string;
  /** The product tile the article resolved to; null when the tenant has no product for it. */
  product_id: string | null;
  /** The tile's Kategorie — what the deep link into the Preisspiegel needs for a DERIVED tile. */
  kategorie_id: string | null;
  amount: number;
  valued: boolean;
  basis: InputPostenBasis;
  /** Null exactly when ``valued``; a named reason otherwise. */
  reason: InputRefusal | null;
  /** True iff the € comes from the curated reference book — always shown as „geschätzt". */
  estimate: boolean;
  quantity: number | null;
  quantity_unit: string | null;
  unit_price?: number;
  price_unit?: string;
  /** The Erntejahr the list price was read for (``list_price``), or the estimate's year. */
  price_year?: number;
  /** The month the OBSERVED price came from; differs from the op's month under a carry-forward. */
  price_month?: string;
  /**
   * HOF-509 — the observation's OWN day, on the dated tier. Present on a list quote (its
   * annual grain) and on a curated reference price (its ``as_of``); absent on the monthly
   * tier, which states ``price_month``, and on a refusal.
   *
   * Read it rather than deriving a day from ``price_year``: that derivation is exact only for
   * a list quote, and silently opens an empty January cell for a curated row (codex r1, P1).
   * A fact written before HOF-509 carries no key, so the derivation stays as the fallback.
   */
  price_date?: string;
  carried_from?: string;
  observation_count?: number;
  counterparty_count?: number;
  /** The price's provenance — a Preisspiegel cell, a list-year, or the estimate's URL. */
  source?: string;
}

/** HOF-307 — the Einzelposten behind ``direct_costs.duenger`` or ``.psm``. */
export interface InputPostenBlock {
  basis: string;
  valued_count: number;
  refused_count: number;
  estimated_count: number;
  /** False when the farm's own purchase prices could not be read this pass (the tier was skipped). */
  kauf_read_ok: boolean;
  items: InputPostenItem[];
}

/**
 * One Leistung Einzelposten — Menge × Preisspiegel-Monatspreis, or the reason there is
 * no price.
 *
 * ``amount`` is authoritative and is never re-multiplied here: re-deriving it in the
 * browser would re-implement the dt-t conversion the worker owns, and a UI that
 * re-derives can disagree with the DB1 the whole cascade is built on. Same contract
 * ``LeistungEstimate`` states one block up, for the same reason.
 *
 * **HOF-102 — on a REFUSED Posten ``amount`` is 0, not ``booked_amount``.** That is the
 * inversion the whole change is: booked revenue is no longer an input to Leistung by any
 * route, so a Posten the price book cannot answer for contributes nothing and the cycle
 * says so (``leistung_incomplete``). ``booked_amount`` rides along as EVIDENCE — what the
 * allocator once put there — and is never arithmetic. The refusals stay in the list
 * deliberately: a drawer that dropped them would hide exactly which kilos went unpriced,
 * and a farmer who cannot see that cannot judge the number at all.
 */
export interface LeistungPostenItem {
  operation_id: string;
  /** The Posten's OWN month (``YYYY-MM``); null when it carries no usable date. */
  month: string | null;
  amount: number;
  booked_amount: number;
  valued: boolean;
  basis: PostenBasis;
  /** Null exactly when ``valued``; a named reason otherwise. */
  reason: ValuationRefusal | null;
  quantity: number | null;
  quantity_unit: string | null;
  /** The price and ITS unit, verbatim off the fact. Never defaulted — see ``LeistungEstimate``. */
  unit_price?: number;
  price_unit?: string;
  catalog_item_id?: string;
  /**
   * The month the PRICE came from. Differs from ``month`` exactly under a carry-forward,
   * which is why both exist: „im August bewertet" and „mit dem Juni-Preis bewertet" are
   * different claims and the hover must be able to make the second one.
   */
  price_month?: string;
  carried_from?: string;
  /** What the average was made of — the numbers behind „Ø aus N Beobachtungen / M Abnehmern". */
  observation_count?: number;
  counterparty_count?: number;
  source?: string;
}

/**
 * HOF-102 — the Einzelposten behind the Leistung, and NOT a second cascade.
 *
 * HOF-21's block carried a whole parallel ``leistung``/``dkfl``/``db1``/``db2``/
 * ``db1_per_ha`` so a client-side switcher could swap the two. There is no switcher and
 * no second cascade: ``row.leistung`` IS this block's Σ (less ``leistung_deductions``),
 * so a client that re-derived the cascade here would be a second implementation of a
 * number that now has exactly one.
 *
 * What remains is what the drawer cannot derive — per-Posten price, month, item and
 * refusal reason — plus the counts the marker reads.
 */
export interface LeistungPostenBlock {
  basis: string;
  /**
   * What the farm had BOOKED on these Posten before the change — the allocator's booth
   * share. Evidence for the backfill's before/after report, never arithmetic: it is not
   * an input to any € on this page.
   */
  leistung_booked: number;
  valued_count: number;
  refused_count: number;
  /**
   * True when at least one price in the blend came from a cell with no declared VAT
   * basis (HOF-45 is the proper fix). Until then the € may be gross, and the drawer
   * says so rather than leaving the caveat in a worker log.
   */
  basis_unknown: boolean;
  items: LeistungPostenItem[];
}

/**
 * #2608 (#1646 R5) — how the ``establishment_amortised`` € on this fact was arrived at.
 *
 * ``amortised``      — a ``planted_at`` anchor exists, so the establishment total was spread
 *                      straight-line over ``[planted_year .. end_year]`` and this crop-year
 *                      took its share.
 * ``expensed_whole`` — no anchor, so the engine charges the FULL total in every materialised
 *                      period (never silently dropping a real cost). Saying "über N Jahre
 *                      verteilt" over that € would be a flat lie, so the two are typed apart.
 */
export const ESTABLISHMENT_BASES = ['amortised', 'expensed_whole'] as const;
export type EstablishmentBasis = (typeof ESTABLISHMENT_BASES)[number];

/**
 * #2608 (#1646 R5) — the window behind the ``Etablierung (anteilig)`` line's €.
 *
 * Present only when the fact books an amortised establishment cost (``establishment_amortised
 * > 0``); the drawer previously showed a bare € with nothing to say what it was a share OF.
 * Every year field is nullable — on ``expensed_whole`` they are ALL null (there is no window),
 * and a malformed one degrades to null rather than NaN.
 */
export interface EstablishmentDetail {
  basis: EstablishmentBasis;
  /** The crop_cycle's ``planted_at`` as stored (a date string); null on ``expensed_whole``. */
  planted_at: string | null;
  planted_year: number | null;
  /** The LAST crop-year the cost amortises over (a ``cleared_at`` wins over the nominal life). */
  end_year: number | null;
  /** The resolved window span in years — what the engine actually divided by. */
  lifetime_years: number | null;
  /** 1-based Standjahr ordinal within the window; null outside it (and on ``expensed_whole``). */
  stand_year: number | null;
}

/**
 * #2487 Part C — one divergence class of the field-vs-revenue product reconciliation:
 * ``crop_mismatch`` (a revenue product resolves to a different crop than the field grows),
 * ``resale_mixed`` (bought-in resale / a generic own+bought-in SKU booked against the cycle),
 * ``revenue_without_yield`` (revenue attributed with (near-)zero harvest labour or yield —
 * the #2375 resale phantom, folded in), or — since Part E — ``variety_mismatch`` (an external
 * variety source, FruitForecast, read a different Sorte on this planting than the twin
 * recorded from MyFarm24/wiberry). Unknown tokens drop out of the mapper.
 */
export type ProductInconsistencyReason =
  | 'crop_mismatch'
  | 'resale_mixed'
  | 'revenue_without_yield'
  | 'variety_mismatch';

/**
 * #2487 Part E — the two disagreeing Sorten behind a ``variety_mismatch``. Both sides are
 * already canonical (the #2322 grammar fold-out plus the FruitForecast borrow map), so they
 * are directly comparable. Which side is right is a product decision — the UI states the
 * disagreement, it never picks a winner.
 */
export interface VarietyDivergence {
  /** The Sorte the twin carries on the crop_cycle (null when none was recorded). */
  field_variety: string | null;
  /** The Sorte the external source read for the same planting. */
  external_variety: string | null;
  /** Canonical source-system id of the external reading (e.g. "fruitforecast"). */
  external_source: string | null;
}

/**
 * #2487 Part C — one diverging revenue product surfaced on the ``product_inconsistency``
 * signal: the product name, the crop the wiberry crosswalk resolved it to (null for pure
 * resale / Handelsware), and its crosswalk origin (own | mixed | resale).
 */
export interface RevenueProduct {
  product: string;
  crop: string | null;
  origin: string | null;
}

/**
 * #2487 Part C — the field-vs-revenue product reconciliation folded onto the fact when the
 * grown crop and the product(s) behind the attributed revenue diverge. One explainable state
 * that generalises the #2375 resale_suspected badge and the wiberry unallocated_booth_crops
 * concept. Present only when inconsistent (key-absence == reconciled); ``reasons`` is never
 * empty when present. Drives the drawer's "Umsatz passt nicht zur Kultur" honesty note.
 */
export interface ProductInconsistency {
  /** The grown crop the revenue was reconciled against. */
  field_crop: string | null;
  /** The divergence classes that fired (sorted, deduped, known tokens only). */
  reasons: ProductInconsistencyReason[];
  /** The diverging revenue products (empty for a pure revenue_without_yield signal). */
  revenue_products: RevenueProduct[];
  /**
   * #2487 Part E — the two disagreeing Sorten. Present ONLY when ``variety_mismatch`` fired,
   * so a pre-Part-E signal keeps its exact shape.
   */
  varieties?: VarietyDivergence;
}

/** #2773 E — the two-sided plausibility band a breach was judged against; sides null-degrade. */
export interface PerBasisImplausibleBand {
  min: number | null;
  max: number | null;
}

/** One Bezugsgröße on which DB1 fell outside its band — the Rechenweg of a single breach. */
export interface PerBasisImplausibleBreach {
  /**
   * The basis the band judged: ``"kg"`` or ``"plant"`` since 2026-10-06 (``MarginBasis``
   * tokens, so the unit re-judgement can recompute the figure). A fact not rematerialised
   * since then still carries ``"ha"``, which is equally a ``MarginBasis`` and reads the same.
   */
  basis: string | null;
  /** The per-basis DB1 value that breached. */
  value: number | null;
  band: PerBasisImplausibleBand | null;
}

/**
 * #2773 E (worker-stamped, conditional) — the ``per_basis_implausible`` block folded onto the
 * fact when DB1 je kg or je Pflanze fell outside the crop's opt-in plausibility band
 * (``db1_bands`` in ``config/catalog/preisspiegel@v1.yaml``). The KEY's presence is the
 * worker's verdict; every detail field is nullable so the mapper can degrade a malformed
 * block to nulls while keeping the object — losing the Rechenweg is an acceptable degrade,
 * losing the flag is not (the #2695 E discipline).
 *
 * The block itself is the FIRST breach in the worker's fixed kg → plant order.
 */
export interface PerBasisImplausible extends PerBasisImplausibleBreach {
  /** The canonical crop key the band resolved on (e.g. "erdbeeren"). */
  band_key: string | null;
  /**
   * The OTHER basis, when it breached as well — present only then. Not decoration: the
   * Hauptschlag re-judgement may clear a unit only if every stamped basis is back in band.
   */
  also?: PerBasisImplausibleBreach[];
}

/**
 * #2769 (worker-stamped, conditional) — the coverage evidence behind ``cost_coverage_low``:
 * how many of the cycle's operations carry any cost (``ops_costed / ops_total = ratio``) and
 * which direct-cost buckets are entirely empty. Emitted together with the flag; fields
 * null-degrade defensively (never NaN).
 */
export interface CostCoverage {
  ops_total: number | null;
  ops_costed: number | null;
  ratio: number | null;
  empty_buckets: string[];
}

/** One ranked-table row = one deckungsbeitrag fact, display-joined with its crop_cycle. */
export interface DeckungsbeitragRow {
  derived_fact_id: string;
  /** The fact's ``entity_id`` — the crop_cycle this margin belongs to. */
  crop_cycle_id: string;
  /** ``crop_year`` string (e.g. "2026") or null (blank crop_year — bucketed as "Ohne Jahr"). */
  period: string | null;
  crop: string;
  field_id: string | null;
  business_area_id: string | null;
  /** Fact-time area the per-ha figure was computed on — never backfilled from the entity. */
  area_ha: number | null;
  /**
   * #2303 — Reihenmeter denominator (je Reihenmeter). Null when the crop_cycle
   * carries no row geometry (key-absence on the fact == basis unavailable).
   */
  row_length_m: number | null;
  /**
   * #2303 — Pflanzenzahl denominator (je Pflanze). Null when the crop_cycle
   * carries no planting density.
   */
  plant_count: number | null;
  /** #2303 — how ``row_length_m``/``plant_count`` were derived; null when absent. */
  geometry_provenance: GeometryProvenance | null;
  leistung: number;
  yield: number;
  /** #2168 — the yield's native unit ("dt" arable / "kg" berry). Null on pre-#2168 facts → "kg". */
  yield_unit: string | null;
  direktzahlung: number;
  direct_costs: DirectCosts;
  establishment_amortised: number;
  /**
   * #2608 (#1646 R5) — the window the ``establishment_amortised`` € is a share of. Null when
   * the fact books no amortised establishment cost, and on every pre-#2608 fact (the key is
   * additive, so prod stays null until the post-deploy re-materialise).
   */
  establishment_detail: EstablishmentDetail | null;
  allocated_overhead: number;
  dkfl: number;
  db1: number;
  db2: number;
  /** Ranking column. Null when ``area_ha`` was unknown at compute time. */
  db1_per_ha: number | null;
  /** True → "ohne Gemeinkosten" honesty badge (no shared-overhead allocation folded in). */
  incomplete_on_shared_overhead: boolean;
  source: 'operations' | 'category_fallback';
  /** How many raw lines the engine summed — the provenance figure (Q4). */
  source_line_count: number;
  /**
   * #2442 — the persisted Gemeinkosten audit array (materialiser line 601). Empty
   * on a fact with no allocated overhead; drives the Gemeinkosten line breakdown.
   */
  allocations: AllocationEntry[];
  /** The four D2 pool lines behind ``allocated_overhead``; empty when none is captured. */
  d2_pools: D2PoolEntry[];
  /** The unpinned-diesel share folded into ``direct_costs.maschinen``; null when none. */
  fuel_allocation: PooledHaAllocation | null;
  /** The tunnel-energy share folded into ``direct_costs.sonstige``; null when none. */
  energy_allocation: PooledHaAllocation | null;
  /** Pots this period allocated to nobody — reported, never smeared. */
  unallocated_pools: UnallocatedPool[];
  /**
   * #2441/#2442 — the Saatgut/Pflanzgut estimate valuation (present only when the
   * seed cost was estimated from the price book, not booked/amortised). Drives the
   * Saatgut line's formula breakdown + the "geschätzt" badge; null when booked or n/a.
   */
  saatgut_estimate: SaatgutEstimate | null;
  /**
   * #2695 E — true when ``leistung`` is a MODELLED estimate from the curated sale price book
   * rather than booked revenue. The single labelling gate for the whole app.
   *
   * Deliberately NOT derived from ``leistung_estimate != null``: the mapper sets it whenever
   * EITHER the fact's flag or a usable block arrives, so a block that fails to map loses the
   * Rechenweg but never the warning, and a block can never reach the screen unlabelled.
   * Under-labelling is the product failure this issue exists to prevent.
   */
  leistung_estimated: boolean;
  /**
   * #2695 E — the operands behind that estimate (Menge × Buchpreis, both with their own unit),
   * or null when the fact carries none / carries one this app could not render honestly.
   * Drives the Leistung line's Rechenweg; the ``leistung_estimated`` warning stands without it.
   */
  leistung_estimate: LeistungEstimate | null;
  /**
   * HOF-518 — true when the Landkosten line is a curated €/ha reference rate rather than a
   * rate derived from the farm's own Pachtvertrag.
   *
   * Tied to the price GRADE, not to "the line was computed": a contract-derived rate is
   * still spread flat over every hectare (which is what ``land_estimate.basis`` says) and is
   * still not a guess. Labelling both the same way would make the drawer unable to tell a
   * farmer which of the two they are reading.
   */
  land_estimated: boolean;
  /**
   * HOF-518 — the operands behind the Landkosten line (Fläche × €/ha), or null when the fact
   * books no land cost. Drives the line's Rechenweg; ``land_estimated`` stands without it.
   */
  land_estimate: LandEstimate | null;
  /**
   * The Substrat charge — true when the substrate inside ``direct_costs.sonstige`` is an
   * estimate (either operand). The fact's own flag OR a usable block, the ``land_estimated``
   * discipline: a block this app rejected must still carry its „geschätzt" label.
   */
  substrat_estimated: boolean;
  /** The operands behind the Substrat row under Sonstige, or null when the fact charges none. */
  substrat_estimate: SubstratEstimate | null;
  /**
   * The engine could not read the substrate price book completely. The worker suppresses
   * such a fact, so this never reaches the drawer in practice; typed so a fact that does
   * carry it is not silently dropped. Absent unless set.
   */
  substrat_price_read_incomplete?: boolean;
  /**
   * Layer C — true when any part of ``direct_costs.investition`` is an estimate (a template
   * rate, or a farm pool split by area). The fact's own flag OR the block's, the
   * ``land_estimated`` discipline: a block this app rejected must keep its label.
   */
  investition_estimated: boolean;
  /** The classes behind the Investition line and the farm pools no cycle carries, or null. */
  investition: InvestitionBlock | null;
  /**
   * The engine could not read the installation book completely. The worker suppresses such a
   * fact, so this never reaches the drawer in practice; typed so it is not silently dropped.
   */
  investition_read_incomplete?: boolean;
  /**
   * Labour-flow wave 2 — was labour MEASURED on this cycle at all?
   *
   * This is the flag every labour line's rendering turns on, and it is NOT
   * `pflueck > 0`: a cycle can legitimately have recorded labour that nets to zero, and a
   * cycle with no records at all must show „noch nicht erfasst" rather than 0,00 €. The
   * two claims are different and a farmer reads them differently — 0,00 € says "we paid
   * nobody", the flag says "nobody wrote it down".
   */
  labour_observed: boolean;
  /** The monthly evidence behind each labour bucket, or null when there is none. */
  labour_posten: LabourPosten | null;
  /** Labour money that reached no line, or null when every euro was booked. */
  labour_unbooked: LabourUnbooked | null;
  /** HOF-644 — wage booked without its employer cost, or null when every wage found its rate. */
  labour_uplift_gap: LabourUpliftGap | null;
  /** The Verpackung material's price evidence and gaps, or null when no material was booked. */
  verpackung_material: VerpackungMaterial | null;
  /** True when the Kühlung figure came off the reference rate rather than a confirmation. */
  kuehlung_estimated: boolean;
  /** The operands behind the Kühlung line (kg × €/kg), or null when nothing priced it. */
  kuehlung_estimate: KuehlungEstimate | null;
  /**
   * The Schichten view's subtotal: `leistung` less the yield-dependent handling
   * (Pflücklohn, Nebenkosten, Verpackung, Kühlung). Read off the fact rather than
   * re-derived here, so the app and the engine can never disagree about its rounding.
   */
  leistung_nach_pfluecken: number;
  /**
   * HOF-102 — the Einzelposten behind ``leistung``, or null when the fact carries none /
   * carries one this app could not render honestly.
   *
   * There is no display state to pair it with any more. ``leistung`` IS the valued figure,
   * on every row, for every reader — the ``leistung_valued`` flag existed only to say which
   * of two modes a row was showing, and there is one mode. A row with no block still shows
   * its ``leistung``; it just cannot break it down.
   */
  leistung_posten: LeistungPostenBlock | null;
  /**
   * HOF-102 (D2) — the cycle's Leistung is knowingly SHORT: at least one Posten's Menge
   * could not be priced and contributed 0 €. Rendered like „Kosten fehlen", because it is
   * the same claim on the other side of the cascade.
   *
   * Key-absence == not flagged, so this defaults false rather than being derived from the
   * block: a fact whose block this app rejected must still carry the warning.
   */
  leistung_incomplete: boolean;
  /** How many Posten that was. 0 when the row is not incomplete. */
  leistung_unpriced_posten: number;
  /**
   * HOF-102 (D1) — at least one price folded a cell with no declared VAT basis, so the €
   * may be gross. Bounded (~7–10 % on berries) and visible rather than silent; HOF-45 nets
   * the sell side at mint time and retires the flag.
   */
  leistung_basis_unknown: boolean;
  /**
   * HOF-102 — the ``sales_commission`` / ``sales_discount`` € withheld from what the buyer
   * paid, already subtracted from ``leistung``.
   *
   * On the fact because the drawer cannot derive it: the valuation replaces the GROSS and
   * the deduction is applied after, so ``Σ leistung_posten.items`` exceeds ``leistung`` by
   * exactly this, and a deduction has no operation of its own to explain the gap. 0 when
   * the cycle has none (key-absent).
   */
  leistung_deductions: number;
  /**
   * HOF-307 — the Einzelposten behind ``direct_costs.duenger`` / ``.psm`` (the material
   * inputs the buy-side cascade valued), or null when the fact carries none / carries one
   * this app could not render honestly. Both blocks render under the ONE „Dünger/PSM"
   * cascade line (``sourceBreakdown.ts::DUENGER_PSM_LINE``).
   */
  duenger_posten: InputPostenBlock | null;
  psm_posten: InputPostenBlock | null;
  /**
   * HOF-519 — the Einzelposten behind ``direct_costs.maschinen``: one per machinery pass of a
   * ``field_work`` booking, the MACHINE as the Posten's article and the pass's own worked area
   * (or its hours) as the Menge. Same block shape and same cascade as the material lines —
   * a work is the same question asked about a different column of the same journal.
   */
  maschinen_posten: InputPostenBlock | null;
  /** HOF-307 — the bucket is knowingly SHORT: N inputs contributed 0 € (the fact's own flag). */
  duenger_incomplete: boolean;
  psm_incomplete: boolean;
  maschinen_incomplete: boolean;
  duenger_unpriced_posten: number;
  psm_unpriced_posten: number;
  maschinen_unpriced_posten: number;
  /** HOF-307 — at least one input in the bucket is priced from the curated reference book. */
  duenger_estimated: boolean;
  psm_estimated: boolean;
  maschinen_estimated: boolean;
  /**
   * #2487 Part C — the field-vs-revenue product reconciliation (present only when the grown
   * crop and the revenue product(s) diverge). Drives the drawer's inconsistency note; null
   * when reconciled (the common case).
   */
  product_inconsistency: ProductInconsistency | null;
  /**
   * #2773 D (worker-stamped, conditional) — the cycle's CANONICAL crop (InVeKoS folds
   * applied worker-side) is on the ``non_marketable_crops`` allowlist (Grünland, Brache, …):
   * definitionally without a Deckungsbeitrag, never merely "unvollständig". Key-absence ==
   * marketable — prod facts simply lack the key until #2773 D lands + rematerialises, and the
   * classifier treats that as not flagged (degraded, correct, never wrong).
   */
  not_marketable?: boolean;
  /**
   * #2773 E (worker-stamped, conditional) — DB1 per the crop's canonical basis breached its
   * opt-in plausibility band. Presence == flagged (the classifier reads only presence);
   * details are the drawer's Rechenweg and null-degrade individually.
   */
  per_basis_implausible?: PerBasisImplausible | null;
  /**
   * #2769 (worker-stamped, conditional) — the cycle's cost base is structurally under-covered
   * (most operations carry no cost), so DB1 overstates the margin. EXACT-token contract:
   * the worker emits the key ``cost_coverage_low`` (#2769 Ask 3) and this field + the
   * classifier's reason token are pinned to that string on both sides. Sole-ownership ruling
   * (5.5 review): #2773 B owns the FE side; #2769 b ships NO FE edits and lands the producer
   * later — key-absence == not flagged keeps the contract safe in either landing order.
   */
  cost_coverage_low?: boolean;
  /** #2769 — the coverage evidence (ops_total / ops_costed / ratio / empty_buckets) behind the flag. */
  cost_coverage?: CostCoverage | null;
  /**
   * The Sorte this row's Deckungsbeitrag was priced and is labelled at.
   *
   * HOF-286 — fact-carried FIRST, entity-joined second. The worker stamps the Sorte its
   * Pflanzgut resolution used (``crop_cycle.variety``, else FruitForecast's
   * ``variety_external`` where the two do not conflict); ``withCropCycle`` fills it from the
   * cycle when the fact carries none, which is every fact written before that change.
   */
  variety: string | null;
  /**
   * HOF-195 — the ROLLUP GROUPING string off the row's ``field`` entity (#2222,
   * ``config/ontology/field.yaml``: *"deliberately NOT an identity"*), display-joined
   * from the ``field`` entities ``useDeckungsbeitrag`` already fetches for ``fieldNames``.
   * Null when the row has no ``field_id``, when the id resolves to no entity, or when
   * that entity carries no ``hauptschlag`` — three different absences with one honest
   * answer, because none of them may put a row into an analysis unit.
   *
   * It is the KEY of the Hauptschlag analysis unit and nothing else: no ranking, no
   * identity, no sum reads it. Grouping additionally requires the string to be on the
   * tenant's curated opt-in list (``deckungsbeitrag_analysis_units``) — the raw value
   * demonstrably over-groups on WF today, where ``2- Autobahn Tunnel`` spans four
   * separate register fields of one Kultur and Sorte that Max never asked to unite.
   */
  hauptschlag: string | null;
  /**
   * #2574 — the crop_cycle's OWN ``crop_year``, joined from the entity (facts don't carry
   * it; null when the cycle is unjoined). NOT a display field: it is the only signal that
   * separates a fact for the cycle's own year — whose served operations really are its
   * operations — from a #1720 thin per-year establishment fact for a LATER year, whose
   * served operations belong to another period. See ``operationsAttributable``.
   */
  cycle_crop_year: string | null;
}

/** Display join source — the flat crop_cycle entity row from ``queryEntities``. */
export interface CropCycleInfo {
  crop_cycle_id: string;
  crop: string | null;
  variety: string | null;
  area_ha: number | null;
  field_id: string | null;
  business_area_id: string | null;
  /** #2574 — the cycle's own ``crop_year`` (the DB rollup axis); null when unset. */
  crop_year: string | null;
}

/**
 * One Kultur+Sorte rollup group (#2302) — Level 1 of the tree table AND the
 * chart's default (Kultur) aggregation, so the two can never disagree. A
 * query-time aggregation, never an identity.
 */
export interface KulturRollupRow {
  /** Stable group key (``kulturGroupKey``) — the chart bar id and the table expansion key. */
  group_key: string;
  /** Display crop name — blank crops arrive as "Unbenannte Kultur". */
  crop: string;
  variety: string | null;
  memberCount: number;
  /** Σ area over members — null unless EVERY member row carries an area. */
  area_ha: number | null;
  leistung: number;
  direktzahlung: number;
  direct_costs: DirectCosts;
  establishment_amortised: number;
  db1: number;
  db2: number;
  /** round2(Σdb1 / Σarea_ha) — only with full member area coverage, else null. */
  db1_per_ha: number | null;
  /** True if ANY member is incomplete on shared overhead. */
  incomplete_on_shared_overhead: boolean;
  /**
   * #2695 E — true if ANY member's Leistung is a price-book estimate, so the group's Σ
   * Leistung is part-modelled. ``some``, not ``every``, for the same reason
   * ``incomplete_on_shared_overhead`` uses it: one un-booked member already makes the
   * displayed sum something other than money received.
   */
  leistung_estimated: boolean;
  /**
   * HOF-102 — true if ANY member's Leistung is knowingly short (a Posten went unpriced),
   * so the group's Σ Leistung understates. ``some`` for exactly the reason above: one
   * incomplete member already makes the displayed sum smaller than what was harvested.
   */
  leistung_incomplete: boolean;
  /** Whole-group revenueless (``classifyGroupSums`` over the sums, #2773 A) — muted chart bar. */
  pending: boolean;
  /** Leaf rows for Level 2, pre-ranked by ``rankByDb1PerHa``. */
  members: DeckungsbeitragRow[];
  /**
   * HOF-195 — what Level 2 RENDERS: the same population as ``members``, but with any
   * Hauptschlag analysis unit folded into one child that expands to its blocks.
   *
   * ``members`` deliberately stays the flat leaf list. Everything that computes over the
   * group — ``db1PerBasisForGroup``, ``classifyGroup``, the chart — keeps reading it, so
   * the Kultur grain is arithmetically untouched by the rollup level (criterion 8 is a
   * property of this split, not only of a test). ``rollupByKultur`` emits children
   * one-per-member in member order, so a tenant with no opt-in list renders exactly what
   * it rendered before this issue: ``withAnalysisUnits`` is what rewrites them.
   */
  children: KulturChild[];
}

/**
 * HOF-195 — one Hauptschlag analysis unit: the united view of several register blocks
 * that a farmer keeps apart for an operational reason (irrigation, on WF) while treating
 * them as ONE analytic unit. A query-time aggregation over the members' facts, never an
 * identity and never materialised — the same posture ``KulturRollupRow`` has.
 *
 * The sums mirror ``rollupByKultur``'s honesty rules exactly, so a unit cannot be more
 * confident than a Kultur about the same numbers: ``area_ha`` is Σ only when EVERY member
 * carries one, and ``db1_per_ha`` is the AREA-WEIGHTED ``round2(Σdb1/Σarea)``, never the
 * mean of the members' per-ha values.
 */
export interface AnalysisUnitRow {
  /** Stable key (``analysisUnitKey``) — the table's expansion key. Scoped to the Kultur group. */
  unit_key: string;
  /** The raw ``field.hauptschlag`` string — also the row's display label. */
  hauptschlag: string;
  memberCount: number;
  /** Sum of member areas — null unless EVERY member row carries an area. */
  area_ha: number | null;
  leistung: number;
  direktzahlung: number;
  direct_costs: DirectCosts;
  establishment_amortised: number;
  db1: number;
  db2: number;
  /** round2(Sum db1 / Sum area_ha) — only with full member area coverage, else null. */
  db1_per_ha: number | null;
  /** True if ANY member is incomplete on shared overhead (the ``some()`` fold, unchanged). */
  incomplete_on_shared_overhead: boolean;
  /** True if ANY member's Leistung is a price-book estimate (the ``some()`` fold, unchanged). */
  leistung_estimated: boolean;
  /** True if ANY member's Leistung is knowingly short (the ``some()`` fold, unchanged). */
  leistung_incomplete: boolean;
  /** Block rows, pre-ranked for the active Bezugsgroesse. */
  members: DeckungsbeitragRow[];
}

/**
 * HOF-195 — one Level-2 child of a Kultur group: either a Hauptschlag analysis unit
 * (which expands to its blocks at Level 3) or a bare Schlag leaf, exactly as before.
 *
 * A discriminated union rather than an optional field, so the table cannot render a unit
 * as a leaf by forgetting a branch — ``tsc`` finds it. ``key`` is the React key AND the
 * expansion key: a leaf keys on its ``derived_fact_id`` (what the old Level 2 keyed on),
 * a unit on its ``unit_key``.
 */
export type KulturChild =
  | { kind: 'unit'; key: string; unit: AnalysisUnitRow }
  | { kind: 'row'; key: string; row: DeckungsbeitragRow };
